import React from 'react';
import { Desc, t } from '@sm/intl';
import { Feature, FeatureSetMap } from '~shared/types/pricing/pricingsvc';
import { DetailsFeatureRuleOptions, ItemType } from './types';
import { TEAMS_PACKAGES, INDIVIDUAL_PACKAGES } from '~pricing/lib/constants';
import { Packages } from '~shared/constants/pricing';
import IncludedLink from '~pricing/components/DetailsTable/Categories/IncludedLink';
import CELL_COPY from '~pricing/lib/PackageFeatures/details/cellCopy';

const SETTING_UNLIMITED = 'unlimited';

export function getTeamCollaborationValues(packageName: Packages): boolean | React.ReactElement {
  let value: boolean | React.ReactElement = false;
  if (TEAMS_PACKAGES.includes(packageName)) {
    value = true;
  } else if (
    INDIVIDUAL_PACKAGES.includes(packageName) &&
    (packageName === Packages.AdvantageAnnual || packageName === Packages.PremierAnnual)
  ) {
    value = <IncludedLink />;
  }

  return value;
}

export function getStringValue(feature: Feature): string {
  const values: ItemType[] = Object.values(CELL_COPY);
  // fetch the copy corresponding to the feature setting from the COPY file
  const settingCopy = values.filter(
    (item: ItemType) => item.defaultMessage.toLowerCase() === feature?.setting?.toLowerCase()
  );
  // create a param with the correct type for the `t` function (if settingCopy array is not empty)
  const cellValue: Desc | undefined = settingCopy.length
    ? {
        id: settingCopy[0].id,
        defaultMessage: settingCopy[0].defaultMessage,
      }
    : undefined;
  // translate the value if it exists, otherwise return empty string
  return cellValue ? t(cellValue) : '';
}

export function getUpperCaseValue(feature: Feature): string {
  return feature?.setting ? feature.setting?.toUpperCase() : '';
}

const { MANUAL, MANUAL_RULE } = CELL_COPY;
export const tagValue: Record<Packages, Desc | string> = {
  [Packages.TeamAdvantage]: MANUAL,
  [Packages.TeamPremier]: MANUAL,
  [Packages.Enterprise]: MANUAL_RULE,
  [Packages.PremierAnnual]: MANUAL,
  [Packages.AdvantageAnnual]: MANUAL,
  [Packages.StandardMonthly]: MANUAL,
  [Packages.StandardAnnual]: MANUAL,
  [Packages.StarterMonthly]: MANUAL,
  [Packages.StandardAnnual]: MANUAL,
  [Packages.StarterAnnual]: MANUAL,
  [Packages.Basic]: '',
  [Packages.AdvantageMonthly]: '',
  [Packages.PremierMonthly]: '',
  [Packages.Flex]: MANUAL,
  [Packages.FormsAnnual]: '',
  [Packages.FormsMonthly]: '',
};

export function getTagValue(packageName: Packages): string {
  if (typeof tagValue[packageName] === 'string') {
    return tagValue[packageName] as string;
  }
  return t(tagValue[packageName] as Desc);
}

export function getFormattedAmountValue(feature: Feature, options: DetailsFeatureRuleOptions): string {
  if (feature?.amount) {
    return t(CELL_COPY[feature.name as keyof typeof CELL_COPY], {
      amount: feature.amount.toLocaleString(options?.locale),
    });
  }
  return '';
}

export function response_count(
  { base_response_count, num_free_responses }: FeatureSetMap,
  options: DetailsFeatureRuleOptions,
  packageName: string
): string {
  let value = '';

  // Annual packages
  if (base_response_count?.amount) {
    value = t(CELL_COPY.base_response_count, { amount: base_response_count.amount.toLocaleString(options?.locale) });

    // Basic/Standard monthly
  } else if (num_free_responses?.amount) {
    const copy = packageName === Packages.Basic ? CELL_COPY.BASIC_NUM_RESPONSES : CELL_COPY.num_free_responses;
    value = t(copy, { amount: num_free_responses.amount.toLocaleString(options?.locale) });

    // Enterprise
  } else if (num_free_responses?.setting === SETTING_UNLIMITED) {
    return t(CELL_COPY.CONTACT_US);
  }
  return value;
}

export function getBaseResponseCount(
  packageName: string,
  feature: FeatureSetMap,
  options: DetailsFeatureRuleOptions
): string {
  return packageName === Packages.Enterprise ? t(CELL_COPY.CONTACT_US) : response_count(feature, options, packageName);
}

export function getCompareResults(packageName: string): boolean | string {
  return packageName === Packages.Basic ? t(CELL_COPY.BASIC_COMPARE_FILTER) : true;
}

export function getTemplatesEnabledValue(packageName: string): string {
  return packageName === Packages.Basic
    ? t(CELL_COPY.BASIC_TEMPLATE_ENABLED_VALUE)
    : t(CELL_COPY.TEMPLATE_ENABLED_VALUE);
}

export function getCreateQuestionLimitValue(feature: Feature): string {
  return t(CELL_COPY.create_question_limit, { amount: feature.amount });
}

export function getCollectorCreateLimitValue(feature: Feature): string {
  return t(CELL_COPY.collector_create_limit, { amount: feature.amount });
}
